import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { cn } from '@/lib/utils';
import { BottomSheet } from '@/design-system/components/BottomSheet/BottomSheet';
import { NAV_BADGE_LABELS, useNavBadges } from '@/app/hooks/useNavBadges';
import { useGuideOptional } from '@/features/guide/useGuide';
import type { NavItem } from './navItems';
import { activeNavItem, visibleWorkspaces, workspaceForPath, workspaceItems } from './navWorkspaces';
import { buildDetail, buildLabel } from '@/lib/buildInfo';

/**
 * The workspace rail, on a phone (2026-08-31).
 *
 * A rail cannot cost 56px of a phone screen, so on small screens it becomes a
 * sheet: the same seven workspaces as chips, the chosen one's pages below.
 * It opens on the current route's workspace, so the first thing someone sees
 * is the neighbourhood they are already standing in.
 *
 * It ADDS to the phone's existing navigation rather than replacing it — the
 * bottom nav (Home, Today, Projects, More) and the /menu page are untouched.
 * Those are the two affordances field staff already know; this is the way to
 * reach a workspace's pages without going through More and reading twenty-odd
 * descriptions.
 */
export function WorkspaceSheet({ onClose }: { onClose: () => void }) {
  const { can, user } = useAuth();
  const location = useLocation();
  const badges = useNavBadges();
  // Optional: these four surfaces are rendered inside GuideProvider in the
  // app, but each is unit-tested on its own — the optional hook returns null
  // instead of throwing, so the nav never depends on the Guide being mounted.
  const guide = useGuideOptional();

  // Operator experience (2026-09-17): the same navWorkspacesFor() filter the
  // desktop rail reads — one workspace, My job.
  const workspaces = visibleWorkspaces(can, user?.experience);
  const routeWorkspaceKey = workspaceForPath(location.pathname, user?.experience)?.key;
  const currentItem = activeNavItem(location.pathname, user?.experience);

  const [selectedKey, setSelectedKey] = useState<string | undefined>(
    () => workspaces.find((w) => w.key === routeWorkspaceKey)?.key ?? workspaces[0]?.key,
  );
  const selected = workspaces.find((w) => w.key === selectedKey) ?? workspaces[0];
  const items = selected ? workspaceItems(selected, can) : [];

  return (
    <BottomSheet title="Workspaces" onClose={onClose}>
      <div className="flex flex-wrap gap-2">
        {workspaces.map((workspace) => {
          const Icon = workspace.icon;
          const isRoute = workspace.key === routeWorkspaceKey;
          return (
            <button
              key={workspace.key}
              type="button"
              onClick={() => setSelectedKey(workspace.key)}
              // The workspace you are IN is marked even while you look at
              // another — the same split the desktop rail makes.
              aria-current={isRoute ? 'true' : undefined}
              aria-pressed={workspace.key === selected?.key}
              className={cn(
                'min-h-touch flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors',
                workspace.key === selected?.key
                  ? 'border-safety-500 bg-safety-500/10 text-safety-500'
                  : 'border-graphite-700 text-graphite-300',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span>{workspace.label}</span>
              {isRoute && <span className="h-1.5 w-1.5 rounded-full bg-safety-500" aria-hidden />}
            </button>
          );
        })}
      </div>

      <ul className="flex flex-col gap-2">
        {items.map((item) => {
          const isActive = currentItem?.to === item.to;
          const row = cn(
            'min-h-touch flex w-full items-center gap-3 rounded-xl border p-3 text-left',
            isActive
              ? 'border-safety-500 bg-safety-500/10 text-safety-500'
              : 'border-graphite-700 bg-graphite-800 text-graphite-100',
          );

          return (
            <li key={item.to}>
              {/* Three kinds of entry, one row: a route, a link out to the
                  portal, and Help — which opens the Guide over this sheet, so
                  the sheet closes first. */}
              {item.external ? (
                <a href={item.to} onClick={onClose} className={row}>{inner(item, badges)}</a>
              ) : item.action === 'guide' ? (
                <button
                  type="button"
                  onClick={() => { onClose(); guide?.setOpen(true); }}
                  className={row}
                >
                  {inner(item, badges)}
                </button>
              ) : (
                <Link
                  to={item.to}
                  onClick={onClose}
                  aria-current={isActive ? 'page' : undefined}
                  className={row}
                >
                  {inner(item, badges)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      {/* Build stamp (2026-09-25): the same line the desktop sidebar carries. */}
      <p className="px-4 pb-2 pt-1 text-center font-mono text-[10px] text-graphite-500" data-testid="build-stamp" title={buildDetail() || undefined}>
        {buildLabel()}
      </p>
    </BottomSheet>
  );
}

/** The row's contents — identical whichever element carries them. */
function inner(item: NavItem, badges: Record<string, number>) {
  const Icon = item.icon;
  const count = item.badge ? badges[item.badge] ?? 0 : 0;
  return (
    <>
      <Icon className="h-5 w-5 shrink-0" aria-hidden />
      <span className="flex-1 truncate">{item.label}</span>
      {item.external && (
        <>
          <ExternalLink className="h-4 w-4 shrink-0 text-graphite-500" aria-hidden />
          <span className="sr-only">{item.hint ?? '(opens the NADC portal)'}</span>
        </>
      )}
      {count > 0 && item.badge && (
        <span
          aria-label={NAV_BADGE_LABELS[item.badge]?.(count) ?? `${count} waiting`}
          className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-safety-500 px-1.5 text-xs font-semibold text-white"
        >
          {count}
        </span>
      )}
    </>
  );
}

export default WorkspaceSheet;
